import React, { FC } from 'react';

import { Container } from '@mui/material';
import { styled } from '@mui/material';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import AnimatedSkeletonMultiline from '~/components/AnimatedSkeletonMultiline';
import Breadcrumbs, { Crumb } from '~/components/Breadcrumbs/Breadcrumbs';
import { SocialShare } from '~/components/SocialShare/SocialShare';
import Avatar from '~/components/UI/Avatar';
import CategoryLabel from '~/components/UI/CategoryLabel/CategoryLabel';
import DifficultyIcon from '~/components/UI/DifficultyIcon/DifficultyIcon';
import { BodyIntroText, BodyText, H1, H3 } from '~/components/UI/Texts';
import FollowButton from '~/components/buttons/FollowButton';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useIsSsku from '~/hooks/tenant/useIsSsku';
import useAuth from '~/hooks/useAuth';
import useFollowExperts from '~/hooks/useFollowExperts';
import {
  SKELETON_TEXT_HEIGHT_PX,
  SKELETON_TEXT_HEIGHT_SMALL_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
} from '~/theme';
import { Maybe, Video } from '~/types';
import sendAnalyticEvent from '~/utils/sendAnalyticEvent';
import { createCategoryLink } from '~/utils/urlUtils/createCategoryLink';

const EXPERT_IMG_TRANSFORMATION = [
  {
    width: 112,
    height: 112,
    focus: 'auto',
  },
];

type HeaderProps = Partial<
  Pick<Video, 'title' | 'description' | 'categories' | 'difficulty' | 'user'> & {
    restrictNavigation: boolean;
    handleClickExpert: () => void;
    loading?: boolean;
    crumbs: Maybe<Crumb[]>;
    className: string;
    followAnalyticEvent?: string;
    shareBlock: boolean;
  }
>;

const HeaderWithExpert: FC<HeaderProps> = ({
  title,
  description,
  user,
  categories,
  difficulty,
  restrictNavigation,
  handleClickExpert,
  loading,
  crumbs,
  className,
  followAnalyticEvent,
  shareBlock = false,
}) => {
  const { userInfo, hasAccessToContent } = useUserInfoContext();
  const { isAuthenticated } = useAuth();
  const {
    followed,
    toggleFollowing,
    loading: loadingToggleFollowing,
  } = useFollowExperts([user?.id]);
  const isSsku = useIsSsku();

  if (loading) {
    return (
      <StyledContainer id="headerWithExpert" className={className}>
        {userInfo && !restrictNavigation && crumbs && (
          <LoadingCrumbs loading data-testid="breadcrumbs" crumbs={crumbs} />
        )}
        <Row>
          <InfoWrapper>
            <LoadingTitle
              xs={SKELETON_TEXT_HEIGHT_PX}
              lines={2}
              lastLineWidth={70}
              spacer={TINY_MARGIN_PX}
            />
            <LoadingExpertBlock>
              <StyledAvatar loading size="sm" squared={isSsku} />
              <ExpertInfo>
                <LoadingExpertTitle xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />
                <LoadingExpertDescription xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />
              </ExpertInfo>
            </LoadingExpertBlock>
            {!isAuthenticated && (
              <LoadingDescription
                spacer={TINY_MARGIN_PX}
                xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
                lines={2}
              />
            )}
          </InfoWrapper>
        </Row>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer id="headerWithExpert" className={className}>
      {userInfo && !restrictNavigation && crumbs && (
        <Breadcrumbs data-testid="breadcrumbs" crumbs={crumbs} />
      )}
      <Row>
        <InfoWrapper>
          <Title special={isSsku} variant="h1" data-testid="videoTitle">
            {title}
          </Title>
          {shareBlock && (
            <SocialShareWrapper isMobile={true}>
              <SocialShare title={title} />
            </SocialShareWrapper>
          )}
          <ExpertBlock data-testid="expertBlock" onClick={handleClickExpert}>
            <StyledAvatar
              src={user?.avatar}
              data-testid="expertImage"
              size="sm"
              noBorder={isSsku}
              squared={isSsku}
              transformation={EXPERT_IMG_TRANSFORMATION}
            />
            <ExpertInfo>
              <StyledH3 variant="h3" data-testid="expertName">
                <ExpertName>{user?.name}</ExpertName>
                {!restrictNavigation && !!userInfo && (
                  <StyledFollowButton
                    loading={loadingToggleFollowing}
                    followed={followed}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!followed && followAnalyticEvent) {
                        sendAnalyticEvent(followAnalyticEvent);
                      }
                      toggleFollowing();
                    }}
                    onlyIcon
                  />
                )}
                {shareBlock && (
                  <SocialShareWrapper>
                    <SocialShare title={title} />
                  </SocialShareWrapper>
                )}
              </StyledH3>
              {user?.job_title && (
                <StyledBodyText data-testid="expertJobTitle">{user.job_title}</StyledBodyText>
              )}
            </ExpertInfo>
          </ExpertBlock>
          {!hasAccessToContent && (
            <Description data-testid="videoDescription">{description}</Description>
          )}
        </InfoWrapper>
        <CategoriesWrapper>
          {categories &&
            categories.map((category) => {
              const categoryLink = !restrictNavigation ? createCategoryLink(category) : undefined;
              return (
                category && (
                  <CategoryLabel
                    key={category.id}
                    name={category?.title}
                    url={categoryLink}
                    withDots={false}
                    data-testid="categoryChips"
                  />
                )
              );
            })}
          {difficulty && (
            <DifficultyIcon difficulty={difficulty} data-testid="videoDifficultyIcon" />
          )}
        </CategoriesWrapper>
        {!hasAccessToContent && (
          <MobileDescription datat-testid="videoDescription">{description}</MobileDescription>
        )}
      </Row>
    </StyledContainer>
  );
};

export default HeaderWithExpert;

const StyledContainer = styled(Container)`
  position: relative;
  z-index: 5;
`;
const Row = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
`;
const InfoWrapper = styled('div')`
  width: 100%;
  max-width: 786px;
  margin-bottom: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: 0;
  }
`;
const CategoriesWrapper = styled('div')`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: -${TINY_MARGIN_PX};
  margin-right: -${TINY_MARGIN_PX};

  &:not(:last-child) {
    margin-bottom: ${SMALL_MARGIN_PX};
  }

  & > * {
    margin-right: ${TINY_MARGIN_PX};
    margin-bottom: ${TINY_MARGIN_PX};
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    justify-content: flex-end;
    max-width: 50%;
  }
`;
const Title = styled(H1)`
  margin-bottom: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-bottom: ${SMALL_MARGIN_PX};
  }
`;
const ExpertBlock = styled('div')`
  display: flex;
  align-items: center;

  &:hover {
    cursor: pointer;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    &:not(:last-child) {
      margin-bottom: ${SMALL_MARGIN_PX};
    }
  }
`;
const LoadingExpertBlock = styled('div')`
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: ${SMALL_MARGIN_PX};
  }
`;
const ExpertInfo = styled('div')`
  flex-grow: 1;
`;
const Description = styled(BodyIntroText)`
  display: none;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: block;
  }
`;
const MobileDescription = styled(BodyIntroText)`
  ${({ theme }) => theme.breakpoints.up('md')} {
    display: none;
  }
`;
const LoadingExpertTitle = styled(AnimatedSkeleton)`
  max-width: 150px;
  margin-bottom: ${TINY_MARGIN_PX};
`;
const LoadingExpertDescription = styled(AnimatedSkeleton)`
  max-width: 200px;
`;
const LoadingTitle = styled(AnimatedSkeletonMultiline)`
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};
`;
const LoadingDescription = styled(AnimatedSkeletonMultiline)`
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${SMALL_MARGIN_PX};
  }
`;
const LoadingCrumbs = styled(Breadcrumbs)`
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};
`;
const StyledFollowButton = styled(FollowButton)`
  margin-left: ${SMALL_MARGIN_PX};
`;
const StyledH3 = styled(H3)`
  display: flex;
  align-items: center;
`;
const StyledBodyText = styled(BodyText)`
  color: ${({ theme }) => theme.palette.common.text.secondaryAccent};
`;
const SocialShareWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>`
  display: ${({ isMobile }) => (isMobile ? 'flex' : 'none')};
  margin-left: none;
  margin-bottom: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-left: auto;
    margin-bottom: 0;
    display: ${({ isMobile }) => (isMobile ? 'none' : 'flex')};
  }
`;
const StyledAvatar = styled(Avatar)`
  margin-right: ${SMALL_MARGIN_PX};
  width: 48px;
  height: 48px;
  min-width: 48px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 56px;
    min-width: 56px;
    height: 56px;
  }
`;
const ExpertName = styled('span')`
  transition: 0.3s color;

  &:hover {
    color: ${({ theme }) => theme.palette.common.expertHeader};
  }
`;
